import { QueryClient, type QueryKey, type UseQueryResult, useQuery } from "@tanstack/react-query"
import type { AxiosError } from "axios"

import { productTypeAdapter } from "@/3514/adapters"
import { EProductTypeQueryKey } from "@/3514/config"
import { productTypeFetcher } from "@/3514/fetchers"
import { type TUserState, useUser } from "@/3514/hooks"
import type { IProductTypeDto, IProductTypeModel } from "@/3514/types"
import { hours, seconds } from "@/3514/utils"

type TProductTypeQuery = UseQueryResult<IProductTypeModel, AxiosError>

function useProductTypeQuery(): ReturnType<() => TProductTypeQuery> {
    const { isLoaded: isUserLoaded }: TUserState = useUser()

    return useQuery<IProductTypeDto, AxiosError, IProductTypeModel, QueryKey>({
        queryKey: EProductTypeQueryKey(),
        queryFn: productTypeFetcher,
        select: productTypeAdapter,
        refetchInterval: hours(2),
        staleTime: hours(2),
        gcTime: hours(2),
        retry: 1,
        retryDelay: seconds(2),
        enabled: isUserLoaded,
        networkMode: "offlineFirst",
        notifyOnChangeProps: ["data", "isSuccess", "isLoading", "refetch", "isFetching", "isError"],
        experimental_prefetchInRender: true
    })
}

async function productTypeQueryPrefetcher({ qc }: { qc: QueryClient }): Promise<void> {
    await qc.prefetchQuery<IProductTypeDto, AxiosError, IProductTypeModel, QueryKey>({
        queryKey: EProductTypeQueryKey(),
        queryFn: productTypeFetcher,
        staleTime: hours(2),
        gcTime: hours(2),
        retry: false
    })
}

export { useProductTypeQuery, productTypeQueryPrefetcher }
export type { TProductTypeQuery }
